import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ApplicationTaskModel } from '../../../models/borrower-portal/application-task-model';
import { AssetService } from '../../../services/asset-service';
import { Observer } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { BorrowerVerificationOrderHistory } from '../../../models/borrower-verification-order-history.model';
import { BorrowerVerificationUserEnrollmentResponse } from '../../../models/borrower-verification-user-enrollment.model';
import { NotificationService } from 'projects/shared/services/notification.service';
import { PlaidCheckService } from '../../../services/plaid-check.service';
import { firstValueFrom } from 'rxjs';
import { PortalServiceFactory } from 'projects/borrower-app/src/app/services/portal-service.factory';

@Component({
    selector: 'app-assets-verification-dialog',
    templateUrl: './assets-verification-dialog.component.html',
    styleUrls: ['./assets-verification-dialog.component.scss'],
    standalone: false
})
export class AssetsVerificationDialogComponent implements OnInit, OnDestroy {

  @Input() applicationId: number;
  @Input() task: ApplicationTaskModel;
  @Input() voaEnabled: boolean = false;
  @Input() plaidCheckEnabled: boolean = false;

  protected provider: "FormFree" | "Truv" | "PlaidCheck" = "FormFree";
  protected thirdPartyEnrollmentWidget: string = "";
  protected usesOrdersApi: boolean = false;
  protected borrowerVerificationOrderId: number | null = null;
  protected loading: boolean = true;
  protected errorMessage: string = "";

  protected showProviderSelection: boolean = false;
  protected plaidCheckConsentGiven: boolean = false;
  protected plaidCheckLoading: boolean = false;

  private _enrollmentInitialized = false;

  constructor(public activeModal: NgbActiveModal,
    private _spinner: NgxSpinnerService,
    private _assetService: AssetService,
    private _notifyService: NotificationService,
    private _cdr: ChangeDetectorRef,
    private _plaidCheckService: PlaidCheckService,
    private _elementRef: ElementRef,
    private readonly _portalServiceFactory: PortalServiceFactory
  ) {}

  ngOnInit(): void {
    this.showProviderSelection = this.voaEnabled && this.plaidCheckEnabled;
    this.updateDialogWidth();

    const observer: Observer<BorrowerVerificationOrderHistory> = {
      next: (order: BorrowerVerificationOrderHistory) => {
        if (order) {
          this.provider = order.integrationProvider as "FormFree" | "Truv";
        }

        this.updateDialogWidth();
        this.setEnrollmentWidget();
      },
      error: (err: any) => {
        if (err.status == 404) {
          this.updateDialogWidth();
          this.setEnrollmentWidget();
        } else {
          this._notifyService.showError("Failed to get verification order. Please, contact your loan contact.", "Error");
          this.activeModal.dismiss();
        }
      },
      complete: () => {
      }
    }

    this._spinner.show();
    this._portalServiceFactory.createBorrower().getVerificationOrderHistoryForTask(this.task.taskId).subscribe(observer)
      .add(() => {
        this._spinner.hide();
      });
  }

  setEnrollmentWidget(): void {
    if (this._enrollmentInitialized) return;
    this._enrollmentInitialized = true;

    // TODO: Need to find provider to use based on task
    this.loading = true;
    this._assetService.initializeBorrowerAssetVerification(this.task.borrowerId, this.applicationId, this.provider).subscribe({
      next: (response: BorrowerVerificationUserEnrollmentResponse) => {
        if (!response.success) {
          this.errorMessage = response.errorMessage || "Unable to initialize asset verification. Please contact your loan officer.";
          this.loading = false;
          this._cdr.detectChanges();
          return;
        }
        this.thirdPartyEnrollmentWidget = response.htmlWidgetSrc;
        this.usesOrdersApi = response.usesOrdersApi;
        this.borrowerVerificationOrderId = response.borrowerVerificationOrderId;
        this.loading = false;
        this._cdr.detectChanges();
      },
      error: (error: any) => {
        this.loading = false;
        this._cdr.detectChanges();
        this._notifyService.showError("Failed to get verification order. Please, contact your loan contact.", "Error");
        this.activeModal.dismiss();
      }
    });
  }

  ngOnDestroy(): void {
    this._plaidCheckService.destroyHandler();
  }

  protected selectProvider(provider: "FormFree" | "Truv" | "PlaidCheck"): void {
    this.provider = provider;
    this.plaidCheckConsentGiven = false;
    this._enrollmentInitialized = false;
    if (provider !== 'PlaidCheck') {
      this.setEnrollmentWidget();
    }
    this.updateDialogWidth();
    this._cdr.detectChanges();
  }

  // Argyle is the ONLY provider that gets the narrow-width + increased-height layout;
  // Truv, FormFree, AccountChek and Plaid keep their original layout untouched. The
  // .modal-dialog lives outside this component's template, so we toggle the class on
  // it directly once the provider is known.
  private updateDialogWidth(): void {
    const dialog = (this._elementRef.nativeElement as HTMLElement).closest('.modal-dialog');
    dialog?.classList.toggle('verification-argyle', (this.provider as string) === 'Argyle');
  }

  protected async startPlaidCheckVerification(): Promise<void> {
    if (!this.plaidCheckConsentGiven) {
      this._notifyService.showError("Please provide consent to continue with Plaid Check verification.", "Consent Required");
      return;
    }

    this.plaidCheckLoading = true;
    this.errorMessage = "";
    this._cdr.detectChanges();

    try {
      const initResponse = await firstValueFrom(
        this._plaidCheckService.initializePlaidCheckForAssets(this.task.borrowerId, this.applicationId)
      );

      if (!initResponse.success) {
        this.errorMessage = initResponse.errorMessage || "Unable to initialize asset verification. Please contact your loan officer.";
        this.plaidCheckLoading = false;
        this._cdr.detectChanges();
        return;
      }

      const linkToken = initResponse.htmlWidgetSrc;
      const result = await this._plaidCheckService.openPlaidLink(linkToken);

      if (result.success) {
        this._notifyService.showSuccess("Bank accounts connected successfully. Verification in progress.", "Success");
        this.activeModal.close();
      } else if (result.cancelled) {
        console.log('User cancelled Plaid Link');
      } else if (result.error) {
        this.errorMessage = this._plaidCheckService.getErrorMessage(result.error);
        this._cdr.detectChanges();
      }
    } catch (error: any) {
      this.errorMessage = error?.message || "Failed to connect bank accounts. Please try again.";
      this._notifyService.showError(this.errorMessage, "Error");
      this._cdr.detectChanges();
    } finally {
      this.plaidCheckLoading = false;
      this._cdr.detectChanges();
    }
  }

  protected onComplete(): void {
    this.activeModal.close();
  }

  protected onClose(): void {
    this.activeModal.close();
  }
}
